<script lang="ts">
  import type { Size } from '$lib/componentTypes';
  import { Modal } from 'bootstrap';
  import { onMount, type Snippet } from 'svelte';

  interface Props {
    title?: string;
    titleSnippet?: Snippet;
    children: Snippet;
    footer?: Snippet<[() => void]>;
    scrollable?: boolean;
    centered?: boolean;
    onclose?: () => void;
    id: string;
    size?: Size;
    fullscreen?: Size;
  }

  let {
    title,
    titleSnippet,
    children,
    footer,
    scrollable = false,
    centered = true,
    onclose,
    id,
    size = 'md',
    fullscreen,
  }: Props = $props();

  let modalEl: HTMLDivElement;
  let bsModal: Modal;

  onMount(() => {
    bsModal = new Modal(modalEl);

    modalEl.addEventListener('hide.bs.modal', () => {
      // if we didn't imperatively remove focus from any active element within the modal, the browser will ignore
      // bootstrap applying the aria-hidden attribute on the modal as it goes away.
      if (document.activeElement?.closest(`#${id}`)) {
        (document.activeElement as HTMLElement).blur();
      }
    });

    if (onclose) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        onclose();
      });
    }

    return () => {
      // for some reason, bootstrap doesn't reset the body inline styles or class when calling dispose if the modal is
      // still open.
      const body = document.querySelector('body') as HTMLBodyElement;
      body.style.overflow = '';
      body.style.paddingRight = '';
      body.classList.remove('modal-open');
      bsModal.dispose();
    };
  });

  export function closeModal() {
    bsModal.hide();
  }

  export function openModal() {
    bsModal.show();
  }
</script>

<div class="modal fade" tabindex="-1" {id} bind:this={modalEl} aria-labelledby="{id}_title">
  <div
    class={['modal-dialog', `modal-${size}`, fullscreen && `modal-fullscreen-${fullscreen}-down`]}
    class:modal-dialog-centered={centered}
    class:modal-dialog-scrollable={scrollable}
  >
    <div class="modal-content">
      {#if title || titleSnippet}
        <div class="modal-header">
          {#if titleSnippet}
            {@render titleSnippet()}
          {:else}
            <h5 class="modal-title" id="{id}_title">{title}</h5>
          {/if}
          <button type="button" class="btn-close" aria-label="Close" onclick={closeModal}></button>
        </div>
      {/if}
      <div class="modal-body">
        {@render children()}
      </div>
      {#if footer}
        {@render footer(closeModal)}
      {/if}
    </div>
  </div>
</div>
